/*
 * apo-stepper.js — TSW Offroad
 * Progressive-enhancement layer that turns Mageworx APO's flat option wall
 * (.mw-apo-container > .mw-option[]) into a guided, one-step-at-a-time flow
 * with a progress rail and an always-visible running total.
 *
 * SAFETY / SCOPE:
 *  - Reads Mageworx's rendered DOM only. Never changes option values, the
 *    variant price, or the $0 base trigger. Never moves inputs out of the form.
 *  - Visibility-only: it toggles which .mw-option blocks are shown per step via
 *    a data attribute + CSS. Mageworx's own conditional hiding (.mw-hidden) is
 *    left untouched and always wins (an option Mageworx hid stays hidden).
 *  - Self-gating: does nothing on any page without .mw-apo-container.
 *  - The running total is summed from Mageworx's own +$ deltas. THIS NUMBER
 *    MUST be reconciled against the real cart total (add-to-cart test) before
 *    this ships to the live theme. Until then, treat it as preview-only.
 */
(function () {
  'use strict';

  // ==========================================================================
  // PRICE TABLE — the ONLY numbers to touch when Mageworx prices change.
  // --------------------------------------------------------------------------
  // These mirror Mageworx "rule" prices that are applied at the cart but are
  // NOT shown on the option (so the theme can't read them and must know them).
  // Flat add-ons that DO show a price on the option (Bushings, Geiser, Billet,
  // Caps, Warranty, Front Setting) are NOT here — the theme reads those live, so
  // Alex can change them in Mageworx with no code change.
  //
  // If Alex edits a rule price in Mageworx (Option sets → [set] → Rules), change
  // the matching number below and re-push the theme, or the sticky total drifts
  // from the cart. See docs/apo-pricing-sync.md for the full mapping.
  // Last synced with Mageworx: 2026-07-15.
  //   pair = 2 shocks (Front or Rear) · set = 4 shocks (Set)
  // ==========================================================================
  var PRICE_RULES = {
    'shock-exchange-order': {
      baseRebuild: { pair: 500, set: 1000 },  // rule "Gen X / Front|Rear|Set"
      valving:     { pair: 30,  set: 90 }      // rule "Alpha|Bravo|Charlie / ..."
    },
    'send-us-your-shocks': {
      baseRebuild: {                            // rules "Gen 1 / ..." & "Gen 2-3 / ..."
        gen1:  { pair: 400, set: 800 },
        other: { pair: 500, set: 1000 }
      },
      valving:      { pair: 60, set: 120 },     // rule "Custom Valving 1|2"
      schrader:     { pair: 50, set: 100 },     // rule "HD Schrader Valves 1|2"
      viton:        { pair: 40, set: 80 },      // rule "Viton Seals 1|2"
      capsDiscount: 60                          // rule "Live valve caps 2 shocks" (-$)
    }
  };

  // --- Per-product config: step map + hidden-rule pricing. The stepper only
  // enhances products configured here; other APO products are left untouched.
  // Step map: each Mageworx option is placed in a step by matching its heading
  // against these substrings (unmatched → fallbackStep). ruleCharges mirrors that
  // product's Mageworx rule set — KEEP IN SYNC if those rules change in the app. ---
  var CONFIGS = {
    // Exchange program — customer swaps cores for pre-built shocks (has deposit).
    'shock-exchange-order': {
      fallbackStep: 3,
      steps: [
        { title: 'Your truck',       sub: 'Generation & fitment',
          match: ['generation', 'year'] },
        { title: 'Your order',       sub: 'Quantity & core deposit',
          match: ['how many', 'cab type', 'clunking', 'core deposit'] },
        { title: 'Condition',        sub: 'Grade of your shocks',
          match: ['cosmetic', 'condition', 'grade'] },
        { title: 'Setup & upgrades', sub: 'Tuning, add-ons, warranty',
          match: ['build time', 'schrader', 'viton', 'bushing', 'valving', 'top hat',
                  'cap kit', 'lift spring', 'warranty', 'billet', 'geiser', 'progressive'] },
        { title: 'Review & agree',   sub: 'Confirm and add to cart',
          match: ['agreement', 'terms', 'photo', 'door jam', 'acknowledg'] }
      ],
      ruleCharges: exchangeRuleCharges
    },
    // Send in your shocks — customer's OWN shocks rebuilt & returned (no deposit).
    'send-us-your-shocks': {
      fallbackStep: 2,
      steps: [
        { title: 'Your shocks', sub: 'Generation & quantity',
          match: ['generation', 'number of shocks', 'front shock setting'] },
        { title: 'Valving',     sub: 'Tuning setup',
          match: ['custom valving'] },
        { title: 'Upgrades',    sub: 'Optional add-ons',
          match: ['schrader', 'viton', 'bushing', 'rear shock guard', 'geiser',
                  'billet', 'live valve cap', 'top hat'] },
        { title: 'Warranty',    sub: 'Coverage & checkout',
          match: ['warranty'] }
      ],
      ruleCharges: sendUsRuleCharges
    }
  };

  // Active config — resolved at init from the product handle.
  var STEPS = [], FALLBACK_STEP = 0, RULE_CHARGES = function () { return 0; };

  function productHandle() {
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : '';
  }

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  ready(function () {
    // Mageworx renders async; wait for the container to appear.
    var tries = 0;
    var poll = setInterval(function () {
      var container = document.querySelector('.mw-apo-container');
      if (container && container.querySelector('.mw-option')) {
        clearInterval(poll);
        init(container);
      } else if (++tries > 40) { // ~8s
        clearInterval(poll);
      }
    }, 200);
  });

  function init(container) {
    if (container.__apoStepper) return; // guard against double-init
    var cfg = CONFIGS[productHandle()];
    if (!cfg) return; // only enhance products we've configured; leave others alone
    container.__apoStepper = true;
    STEPS = cfg.steps;
    FALLBACK_STEP = cfg.fallbackStep;
    RULE_CHARGES = cfg.ruleCharges;

    var state = { current: 0, maxReached: 0, built: false };

    // Hide the legacy floating-summary bar (avoids a double sticky bar).
    document.querySelectorAll('.product-floating-summary').forEach(function (el) {
      el.style.display = 'none';
    });

    tagOptions(container);
    orderAgreement(container);
    var ui = buildChrome(container);
    document.body.classList.add('apo-stepper-on');
    container.classList.add('apo-stepper-active');

    // Jump to any step already reached (Back button, clicking a progress segment).
    function goToStep(idx) {
      if (idx < 0 || idx > state.maxReached || idx >= STEPS.length) return;
      state.current = idx;
      applyStep(container, state.current);
      recompute(container, ui);
      refreshNav(container, ui, state);
      scrollToTop(container);
    }
    state.go = goToStep;
    Array.prototype.forEach.call(ui.segs, function (seg, i) {
      seg.addEventListener('click', function () { goToStep(i); });
    });

    // Re-tag + re-apply after Mageworx mutates (conditional logic re-renders).
    var mo = new MutationObserver(function () {
      tagOptions(container);
      orderAgreement(container);
      applyStep(container, state.current);
      recompute(container, ui);
      refreshNav(container, ui, state);
    });
    mo.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

    ['change', 'input', 'click'].forEach(function (evt) {
      container.addEventListener(evt, function () {
        // let Mageworx update first
        setTimeout(function () {
          recompute(container, ui);
          refreshNav(container, ui, state);
        }, 60);
      });
    });

    // Make the WHOLE option card clickable (the padding/price area looks
    // interactive but only the tiny radio/label was the real target). Clicks that
    // already land on the input or its label pass through to native handling, so
    // there's no double-toggle.
    container.addEventListener('click', function (e) {
      if (e.target.closest('input, label, select, textarea, a, button')) return;
      var card = e.target.closest('.mw-option__value');
      if (!card) return;
      var input = card.querySelector('input[type="radio"], input[type="checkbox"]');
      if (input) input.click();
    });

    // Auto-advance: after a selection, glide down to the next option still needing
    // an answer in this step (or to the Continue button once the step is done).
    container.addEventListener('change', function (e) {
      var changed = e.target && e.target.closest ? e.target.closest('.mw-option') : null;
      setTimeout(function () {
        // Only glide to the NEXT option still needing an answer. When the step is
        // complete, stay put — Continue is right below the last option; scrolling
        // to it would cram everything to the top and look empty.
        var next = nextUnanswered(container, changed); // after Mageworx re-renders
        if (next) { scrollElementBelowRail(next); }
      }, 150);
    });

    applyStep(container, state.current);
    recompute(container, ui);
    refreshNav(container, ui, state);
    state.built = true;
  }

  // --- Assign each .mw-option a data-apo-step based on its heading ---
  // Strong signals that a headingless prose block is the legal Order Agreement
  // verbiage — must land on the final Review & Agree step, next to the "I agree"
  // control. Matched on distinctive phrases, NOT generic words like "cosmetic
  // condition" that also appear inside the agreement body.
  var AGREEMENT_SIGNALS = /placing my order|refund policy|restocking|authorize tsw|deposit and charges|no returns or refunds|ship\W+my original shock cores/i;

  function tagOptions(container) {
    var opts = container.querySelectorAll(':scope > .mw-option');
    var lastStep = STEPS.length - 1;
    opts.forEach(function (opt) {
      if (opt.getAttribute('data-apo-step') != null) return;
      var heading = optionHeading(opt);

      // Headingless prose (Mageworx "paragraph"): keep legal agreement text on
      // the final step; other stray prose goes to the fallback step.
      if (!heading && /type-paragraph/.test(opt.className)) {
        var body = (opt.textContent || '');
        opt.setAttribute('data-apo-step', AGREEMENT_SIGNALS.test(body) ? lastStep : FALLBACK_STEP);
        return;
      }

      var h = heading.toLowerCase();
      var step = FALLBACK_STEP;
      for (var i = 0; i < STEPS.length; i++) {
        if (STEPS[i].match.some(function (m) { return h.indexOf(m) !== -1; })) { step = i; break; }
      }
      opt.setAttribute('data-apo-step', step);
    });
  }

  // On the Review step, show the agreement verbiage BEFORE the "I agree"
  // control (read, then agree). Reorders two siblings within the container;
  // no input leaves the form. Idempotent, so it's safe to call repeatedly.
  function orderAgreement(container) {
    var opts = Array.prototype.slice.call(container.querySelectorAll(':scope > .mw-option'));
    var control = null, para = null;
    opts.forEach(function (o) {
      if (!control && /agreement|terms/i.test(optionHeading(o))) control = o;
      if (!para && /type-paragraph/.test(o.className) && AGREEMENT_SIGNALS.test(o.textContent || '')) para = o;
    });
    if (control && para && (para.compareDocumentPosition(control) & Node.DOCUMENT_POSITION_PRECEDING)) {
      container.insertBefore(para, control); // move verbiage above the control
    }
  }

  function optionHeading(opt) {
    var h = opt.querySelector('.mw-option__title, [class*="title"], label, legend');
    return h ? (h.textContent || '').trim().replace(/\s+/g, ' ') : '';
  }

  // --- Build progress rail (top) + sticky total bar (bottom) ---
  function buildChrome(container) {
    var rail = document.createElement('div');
    rail.className = 'apo-rail';
    rail.innerHTML =
      '<div class="apo-rail__label"><span class="apo-rail__title">Build your order</span>' +
      '<span class="apo-rail__count">Step <b data-apo-now>1</b> of ' + STEPS.length + '</span></div>' +
      '<div class="apo-rail__segs" data-apo-segs></div>' +
      '<div class="apo-rail__step"><span class="apo-rail__stepname" data-apo-stepname></span>' +
      '<span class="apo-rail__stepsub" data-apo-stepsub></span></div>';
    container.parentNode.insertBefore(rail, container);

    var segs = rail.querySelector('[data-apo-segs]');
    for (var i = 0; i < STEPS.length; i++) {
      var s = document.createElement('span');
      s.className = 'apo-seg';
      segs.appendChild(s);
    }

    var bar = document.createElement('div');
    bar.className = 'apo-totalbar';
    bar.innerHTML =
      '<div class="apo-totalbar__meta"><span class="apo-totalbar__k">Order total</span>' +
      '<span class="apo-totalbar__v" data-apo-total>—</span></div>' +
      '<button type="button" class="apo-totalbar__btn" data-apo-next>Continue</button>';
    document.body.appendChild(bar);

    // Back + Continue row directly under the step's options — so "what's next"
    // (and "go back to change something") is obvious in the reading flow, not
    // just tucked in the sticky bar corner.
    var navRow = document.createElement('div');
    navRow.className = 'apo-navrow';
    var back = document.createElement('button');
    back.type = 'button';
    back.className = 'apo-back';
    back.setAttribute('data-apo-back', '');
    back.textContent = '← Back';
    var inline = document.createElement('button');
    inline.type = 'button';
    inline.className = 'apo-inline-next';
    inline.setAttribute('data-apo-inline-next', '');
    navRow.appendChild(back);
    navRow.appendChild(inline);
    container.parentNode.insertBefore(navRow, container.nextSibling);

    return {
      rail: rail,
      segs: segs.children,
      nowEl: rail.querySelector('[data-apo-now]'),
      stepName: rail.querySelector('[data-apo-stepname]'),
      stepSub: rail.querySelector('[data-apo-stepsub]'),
      totalEl: bar.querySelector('[data-apo-total]'),
      nextBtn: bar.querySelector('[data-apo-next]'),
      inlineBtn: inline,
      backBtn: back,
      bar: bar
    };
  }

  // --- Show only current step's options (Mageworx's .mw-hidden still wins) ---
  function applyStep(container, current) {
    container.querySelectorAll(':scope > .mw-option').forEach(function (opt) {
      var step = parseInt(opt.getAttribute('data-apo-step'), 10);
      if (step === current) { opt.setAttribute('data-apo-current', ''); }
      else { opt.removeAttribute('data-apo-current'); }
    });
  }

  // --- Is a step complete? (all required, currently-visible options answered) ---
  function stepComplete(container, stepIdx) {
    var opts = container.querySelectorAll(':scope > .mw-option[data-apo-step="' + stepIdx + '"]');
    for (var i = 0; i < opts.length; i++) {
      var opt = opts[i];
      if (!opt.classList.contains('mw-option--required')) continue;
      if (opt.classList.contains('mw-hidden')) continue; // not currently relevant
      if (!optionAnswered(opt)) return false;
    }
    return true;
  }

  function optionAnswered(opt) {
    var radios = opt.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    if (radios.length) {
      for (var i = 0; i < radios.length; i++) { if (radios[i].checked) return true; }
      return false;
    }
    var sel = opt.querySelector('select');
    if (sel) return !!sel.value && !/please select/i.test(sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text : '');
    var txt = opt.querySelector('input[type="text"], input[type="number"], textarea');
    if (txt) return !!txt.value.trim();
    var file = opt.querySelector('input[type="file"]');
    if (file) return file.files && file.files.length > 0;
    return true;
  }

  // --- Running total. Derived from Mageworx's actual pricing rules for this
  // option set (confirmed 2026-07-15):
  //   total = BASE REBUILD (hidden rule) + sum of the shown option prices.
  // The base rebuild ($500 for 2 shocks / $1000 for a Set of 4) is added by a
  // Mageworx rule keyed on Generation + How Many — it is NOT rendered as an
  // option price anywhere on the page, so we inject it here. Add-ons already
  // show quantity-correct prices (Mageworx swaps the $50/$100 variants by count),
  // so they are summed as-is with NO multiplier. Note: a niche -$60 Billet-cap
  // discount rule is not modeled; edge combos may differ by that amount. ---
  function recompute(container, ui) {
    var shown = 0;
    container.querySelectorAll('input:checked').forEach(function (inp) {
      var row = inp.closest('.mw-option__value, label, li, div');
      var price = row ? row.querySelector('.mw-option__price') : null;
      if (price) shown += parsePrice(price.textContent);
    });
    container.querySelectorAll('select').forEach(function (sel) {
      var o = sel.options[sel.selectedIndex];
      if (o) shown += parsePrice(o.textContent);
    });

    var total = RULE_CHARGES(container) + shown;
    ui.totalEl.textContent = total > 0 ? formatMoney(total) : 'Add options to see price';
    ui.totalEl.setAttribute('data-preview', 'true');
  }

  // ---- Hidden rule-based charges (applied by Mageworx but NOT rendered as option
  // prices on the page). Each mirrors that product's Mageworx rule set. ----

  // Reads all numbers from PRICE_RULES above. Not modeled: niche -$60 Billet-cap
  // discount on some exchange Gen3/Orange combos.
  function exchangeRuleCharges(container) {
    var count = shockCount(container);
    if (!count) return 0;
    var qty = (count >= 4) ? 'set' : 'pair';
    var t = PRICE_RULES['shock-exchange-order'];
    var total = t.baseRebuild[qty];
    if (valvingSelected(container)) total += t.valving[qty];
    return total;
  }

  function sendUsRuleCharges(container) {
    var count = shockCount(container);
    if (!count) return 0;
    var qty = (count >= 4) ? 'set' : 'pair';
    var t = PRICE_RULES['send-us-your-shocks'];
    var gen1 = /gen\s*1|gen1/i.test(selectedValueText(container, /generation/i));
    var total = (gen1 ? t.baseRebuild.gen1 : t.baseRebuild.other)[qty];
    if (valvingSelected(container)) total += t.valving[qty];
    if (optionSelected(container, /schrader/i)) total += t.schrader[qty];
    if (optionSelected(container, /viton/i)) total += t.viton[qty];
    if (count === 2 && optionSelected(container, /live valve cap/i)) total -= t.capsDiscount;
    return total;
  }

  function valvingSelected(container) {
    var opts = container.querySelectorAll(':scope > .mw-option');
    for (var i = 0; i < opts.length; i++) {
      if (!/custom valving/i.test(optionHeading(opts[i]))) continue;
      var sel = opts[i].querySelector('select');
      if (sel) return !!sel.value && !/please select/i.test(sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text : '');
      return !!opts[i].querySelector('input:checked');
    }
    return false;
  }

  // Is an option (matched by heading) currently selected (checkbox/radio checked)?
  function optionSelected(container, headingRe) {
    var opts = container.querySelectorAll(':scope > .mw-option');
    for (var i = 0; i < opts.length; i++) {
      if (headingRe.test(optionHeading(opts[i]))) {
        return !!opts[i].querySelector('input:checked');
      }
    }
    return false;
  }

  // Text of the checked value within the option whose heading matches (for
  // reading which Generation/etc. is selected).
  function selectedValueText(container, headingRe) {
    var opts = container.querySelectorAll(':scope > .mw-option');
    for (var i = 0; i < opts.length; i++) {
      if (!headingRe.test(optionHeading(opts[i]))) continue;
      var chosen = opts[i].querySelector('input:checked');
      if (!chosen) return '';
      var row = chosen.closest('.mw-option__value, label, li, div');
      return row ? row.textContent : '';
    }
    return '';
  }

  // How many shocks the order covers, from the quantity option ("How Many Shocks"
  // on exchange, "Number of Shocks" on send-us). e.g. "Set (4 Shocks)" → 4. 0 if
  // not chosen.
  function shockCount(container) {
    var opts = container.querySelectorAll(':scope > .mw-option');
    for (var i = 0; i < opts.length; i++) {
      if (!/how many|number of shocks/i.test(optionHeading(opts[i]))) continue;
      var chosen = opts[i].querySelector('input:checked');
      if (!chosen) return 0;
      var row = chosen.closest('.mw-option__value, label, li, div');
      var m = (row ? row.textContent : '').match(/(\d+)\s*shock/i);
      return m ? parseInt(m[1], 10) : 0;
    }
    return 0;
  }

  // Only count an explicitly-formatted currency amount ($-prefixed). Parsing the
  // first bare number is wrong for <select> option labels that contain non-price
  // digits, e.g. "Standard Warranty (18 months)" (→ phantom $18) or "Extended
  // Warranty (6 Month) (+$100.00)" (→ $6 instead of $100). Shown prices from
  // Mageworx always carry a "$", so anchoring on it is safe and unambiguous.
  function parsePrice(text) {
    var m = (text || '').match(/\$\s*([\d,]+(?:\.\d{2})?)/);
    return m ? parseFloat(m[1].replace(/,/g, '')) : 0;
  }

  function formatMoney(n) {
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  // --- Progress rail + Continue/Add-to-Cart state ---
  function refreshNav(container, ui, state) {
    var current = state.current;
    ui.nowEl.textContent = current + 1;
    ui.stepName.textContent = STEPS[current].title;
    ui.stepSub.textContent = STEPS[current].sub;

    for (var i = 0; i < STEPS.length; i++) {
      var reached = i <= state.maxReached;
      var cls = 'apo-seg';
      // Green only for a step you've REACHED and finished — never the current
      // step, and never a not-yet-reached step. A step with no required options
      // is vacuously "complete" (e.g. send-us step 3 Upgrades), so gating on
      // stepComplete() alone painted it green before the user got there.
      if (i !== current && reached && stepComplete(container, i)) cls += ' is-done';
      if (i === current) cls += ' is-now';
      if (reached) cls += ' is-clickable'; // jumpable
      ui.segs[i].className = cls;
    }

    var isLast = current === STEPS.length - 1;
    var canAdvance = stepComplete(container, current);
    var disabled = isLast ? !allComplete(container) : !canAdvance;

    function advance() {
      if (isLast) { triggerAddToCart(); return; }
      if (!canAdvance) return;
      state.maxReached = Math.max(state.maxReached, current + 1);
      state.go(current + 1);
    }

    // Sticky-bar button: short label (the running total is the star there).
    ui.nextBtn.textContent = isLast ? 'Add to Cart' : 'Continue';
    ui.nextBtn.classList.toggle('is-atc', isLast);
    ui.nextBtn.disabled = disabled;
    ui.nextBtn.onclick = advance;

    // Inline button under the options: name the next step so it's unmistakable.
    if (ui.inlineBtn) {
      ui.inlineBtn.textContent = isLast
        ? 'Add to Cart'
        : 'Continue to ' + STEPS[current + 1].title + '  →';
      ui.inlineBtn.classList.toggle('is-atc', isLast);
      ui.inlineBtn.disabled = disabled;
      ui.inlineBtn.onclick = advance;
    }

    // Back button: hidden on the first step; jumps to the previous step.
    if (ui.backBtn) {
      ui.backBtn.style.display = current === 0 ? 'none' : '';
      ui.backBtn.onclick = function () { state.go(current - 1); };
    }
  }

  function allComplete(container) {
    for (var i = 0; i < STEPS.length; i++) { if (!stepComplete(container, i)) return false; }
    return true;
  }

  function triggerAddToCart() {
    var btn = document.querySelector('form[action*="/cart/add"] [name="add"], .product-form__submit');
    if (btn) btn.click();
  }

  // First option in the current step that still needs an answer, searching from
  // just after the one the shopper changed (so it moves DOWN the page).
  function nextUnanswered(container, fromOpt) {
    var opts = Array.prototype.slice.call(
      container.querySelectorAll(':scope > .mw-option[data-apo-current]')
    ).filter(function (o) {
      return !o.classList.contains('mw-hidden') && getComputedStyle(o).display !== 'none';
    });
    var start = fromOpt ? opts.indexOf(fromOpt) : -1;
    for (var i = Math.max(0, start + 1); i < opts.length; i++) {
      if (!optionAnswered(opts[i])) return opts[i];
    }
    return null;
  }

  // Scroll an element to just below the sticky rail (shared by auto-advance).
  function scrollElementBelowRail(el) {
    var rail = document.querySelector('.apo-rail');
    var railH = rail ? rail.offsetHeight : 0;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var y = el.getBoundingClientRect().top + window.scrollY - railH - 16;
    window.scrollTo({ top: Math.max(0, y), behavior: reduce ? 'auto' : 'smooth' });
  }

  function scrollToTop(container) {
    // Anchor on the container (NOT the rail — the rail is position:sticky, so its
    // rect.top reads ~0 when stuck and the page barely moves). Land the rail at
    // the top of the viewport with the first option right below it.
    var rail = document.querySelector('.apo-rail');
    var railH = rail ? rail.offsetHeight : 0;
    var y = container.getBoundingClientRect().top + window.scrollY - railH - 10;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  }
})();
